import {
  Archive,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Database,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import type { ProblemType } from '@/db/schema/enums'
import { PROBLEM_TAG_VOCABULARY } from '@/lib/constants/problem-tags'
import {
  distributeItemsAcrossDays,
  formatDateISO,
  getDaysInRange,
  getUpcomingMonday,
  getUpcomingSaturday,
  parseDateISO,
} from '@/lib/date-utils'
import { trpc } from '@/lib/trpc'
import { AddQuestionModal } from '../problems/add-question-modal'
import { ApproachesModal } from '../problems/approaches-modal'
import { EditQuestionModal } from '../problems/edit-question-modal'
import { useCoachingQuestions } from '../problems/hooks/use-questions'
import { MasterQuestionsTable } from '../problems/master-questions-table'
import type {
  CoachingClient,
  CoachingHomeworkWithProblem,
  CoachingQuestion,
  QuestionApproach,
} from '../problems/types'
import type { SourceContentItem } from '../sources/types'
import {
  AssignedHomeworkTable,
  canPushHomeworkToNotion,
  canRetryNotionDelivery,
  deliveryResultsAfterAuthoritativeRefresh,
  hasHomeworkActivationMetadata,
  isHistoricalLegacyHomework,
  mergeAuthoritativeDeliveryStates,
  type NotionDeliveryUiResult,
  pendingNotionDeliveryHomeworkIds,
} from './assigned-homework-table'
import { invalidateCoachingAssignmentQueries } from './hooks/use-coaching-clients'
import { SourceContentWidget } from './source-content-widget'
import { StandaloneTasksWidget } from './standalone-tasks-widget'
import { StudyPlanWidget } from './study-plan-widget'
import { Tabs } from './tabs'

interface HomeworkSectionProps {
  client: CoachingClient
  onAssignHomework: (
    type:
      | 'PROBLEM'
      | 'EXERCISE'
      | 'TASK'
      | 'SYSTEM_DESIGN'
      | 'SOURCE_CONTENT'
      | 'OTHER',
    data: {
      problemId?: string
      exerciseId?: string
      systemDesignProblemId?: string
      taskId?: string
      sourceContentId?: string
      title?: string
      description?: string
      url?: string
      notes?: string
    },
  ) => Promise<void>
  onRemoveHomework: (assignmentId: string) => Promise<void>
  onToggleDoneBefore: (questionId: string, doneBefore: boolean) => Promise<void>
  onUpdateLastAttempt: (
    questionId: string,
    lastAttemptDate: string | null,
  ) => Promise<void>
  onRefresh?: () => void | Promise<void>
}

const NON_SUCCESS_DELIVERY_STATES = [
  'queued',
  'creating',
  'unknown',
  'failed',
  'archived_without_push',
] as const

type NonSuccessDeliveryState = (typeof NON_SUCCESS_DELIVERY_STATES)[number]

function curriculumHref(): string {
  if (typeof window === 'undefined') return '#curriculum'
  const destination = new URL(window.location.href)
  destination.searchParams.set('coachingView', 'v2')
  destination.hash = 'curriculum'
  return `${destination.pathname}${destination.search}${destination.hash}`
}

function selectV2ForCurriculum(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem('bctci:coaching-view', 'v2')
  } catch {
    // Navigation still works when browser storage is unavailable.
  }
}

export function HomeworkSection({
  client,
  onAssignHomework: assignHomework,
  onRemoveHomework,
  onToggleDoneBefore: _onToggleDoneBefore,
  onUpdateLastAttempt: _onUpdateLastAttempt,
  onRefresh,
}: HomeworkSectionProps) {
  const utils = trpc.useUtils()
  const [showTrayNotice, setShowTrayNotice] = useState(false)
  const [observedDeliveryResults, setObservedDeliveryResults] = useState<
    Record<string, NotionDeliveryUiResult>
  >({})
  const assignmentsRef = useRef(client.assignedHomework)
  assignmentsRef.current = client.assignedHomework
  const confirmDeleteAllTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)

  useEffect(() => {
    return () => {
      if (confirmDeleteAllTimeoutRef.current) {
        clearTimeout(confirmDeleteAllTimeoutRef.current)
      }
    }
  }, [])

  const observeDeliveryResult = useCallback(
    (assignmentId: string, result: NotionDeliveryUiResult) => {
      setObservedDeliveryResults((current) => ({
        ...current,
        [assignmentId]: result,
      }))
    },
    [],
  )

  const acceptAuthoritativeDeliveryRefresh = useCallback(
    (assignments: readonly CoachingHomeworkWithProblem[]) => {
      setObservedDeliveryResults((current) =>
        deliveryResultsAfterAuthoritativeRefresh(current, assignments),
      )
    },
    [],
  )

  // Queued/creating deliveries resolve in the worker; poll the authoritative
  // queue states until every observed delivery reaches a terminal state.
  const pendingDeliveryIds = pendingNotionDeliveryHomeworkIds(
    observedDeliveryResults,
  )
  const deliveryStatusQuery = trpc.admin.clients.notionDeliveryStatus.useQuery(
    { id: client.id, homeworkIds: pendingDeliveryIds },
    { enabled: pendingDeliveryIds.length > 0, refetchInterval: 15_000 },
  )
  const authoritativeDeliveries = deliveryStatusQuery.data?.deliveries
  useEffect(() => {
    if (!authoritativeDeliveries) return
    setObservedDeliveryResults((current) =>
      mergeAuthoritativeDeliveryStates(current, authoritativeDeliveries),
    )
  }, [authoritativeDeliveries])

  const refreshAssignmentViews = useCallback(async (): Promise<void> => {
    const previousAssignments = assignmentsRef.current
    await Promise.all([
      invalidateCoachingAssignmentQueries(utils, client.id),
      Promise.resolve(onRefresh?.()),
    ])
    const refreshedAssignments = assignmentsRef.current
    if (refreshedAssignments !== previousAssignments) {
      acceptAuthoritativeDeliveryRefresh(refreshedAssignments ?? [])
    }
  }, [acceptAuthoritativeDeliveryRefresh, client.id, onRefresh, utils])
  const onAssignHomework = useCallback<
    HomeworkSectionProps['onAssignHomework']
  >(
    async (type, data) => {
      await assignHomework(type, data)
      setShowTrayNotice(true)
    },
    [assignHomework],
  )
  const {
    questions,
    sources,
    tags,
    loading,
    loadingMore,
    totalCount,
    addQuestion,
    updateQuestion,
    deleteQuestion,
  } = useCoachingQuestions()

  // Wrapper to convert old questionId signature to new format
  const handleAssignProblem = async (questionId: string) => {
    await onAssignHomework('PROBLEM', { problemId: questionId })
  }

  // Handler for assigning multiple problems at once (from study plans)
  const handleAssignMultipleProblems = useCallback(
    async (problemIds: string[]) => {
      for (const problemId of problemIds) {
        await onAssignHomework('PROBLEM', { problemId })
      }
      // Refresh after all additions
      await refreshAssignmentViews()
    },
    [onAssignHomework, refreshAssignmentViews],
  )

  // Handler for assigning source content (chapters, modules, etc.)
  const handleAssignSourceContent = useCallback(
    async (sourceContentId: string, title: string, url?: string) => {
      await onAssignHomework('SOURCE_CONTENT', { sourceContentId, title, url })
      await refreshAssignmentViews()
    },
    [onAssignHomework, refreshAssignmentViews],
  )

  // Mutation for creating tasks from resources
  const createTaskMutation = trpc.admin.content.createTask.useMutation()

  // Handler for assigning source content items (problems, exercises, tasks, etc.)
  const handleAssignSourceContentItem = useCallback(
    async (item: SourceContentItem, chapterTitle: string) => {
      // Map item type to homework type and extract the appropriate ID/data
      switch (item.contentType) {
        case 'PROBLEM':
          if (item.contentId) {
            await onAssignHomework('PROBLEM', { problemId: item.contentId })
          }
          break
        case 'CODING_EXERCISE':
          if (item.contentId) {
            await onAssignHomework('EXERCISE', { exerciseId: item.contentId })
          }
          break
        case 'SYSTEM_DESIGN':
          if (item.contentId) {
            await onAssignHomework('SYSTEM_DESIGN', {
              systemDesignProblemId: item.contentId,
            })
          }
          break
        case 'TASK':
          if (item.contentId) {
            const taskTitle = item.task?.title || item.title || 'Untitled Task'
            await onAssignHomework('TASK', {
              taskId: item.contentId,
              title: taskTitle,
            })
          }
          break
        case 'RESOURCE': {
          // Resource items - create a task with resourceId, then assign
          const resourceTitle = item.title || 'Untitled'
          const resourceType = item.resource?.type || 'video'
          const prefix = resourceType === 'article' ? 'Read' : 'Watch'
          const formattedResourceTitle = `${prefix}: ${resourceTitle}`
          // Create task via tRPC, then assign as TASK homework
          if (item.contentId) {
            const createdTask = await createTaskMutation.mutateAsync({
              title: formattedResourceTitle,
              url: item.url || undefined,
              estimatedMinutes: item.durationMinutes || undefined,
              resourceId: item.contentId,
            })
            await onAssignHomework('TASK', {
              taskId: createdTask.id,
              title: formattedResourceTitle,
            })
          }
          break
        }
        // VIDEO/ARTICLE are create-only inputs — they resolve to RESOURCE rows
        // and are handled by the 'RESOURCE' case above; they never come back
        // as this contentType on read.
        default:
          console.warn(`Unknown item content type: ${item.contentType}`)
      }
      await refreshAssignmentViews()
    },
    [onAssignHomework, refreshAssignmentViews, createTaskMutation],
  )

  // Handler for assigning standalone tasks (tasks without source associations)
  const handleAssignStandaloneTask = useCallback(
    async (taskId: string, title: string) => {
      await onAssignHomework('TASK', { taskId, title })
      await refreshAssignmentViews()
    },
    [onAssignHomework, refreshAssignmentViews],
  )

  const notionPushMutation = trpc.admin.clients.notionPush.useMutation()
  const archiveHomeworkMutation =
    trpc.admin.clients.archiveHomework.useMutation()
  const [showAddQuestion, setShowAddQuestion] = useState(false)
  const [showEditQuestion, setShowEditQuestion] = useState(false)
  const [showApproachesModal, setShowApproachesModal] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [selectedQuestion, setSelectedQuestion] =
    useState<CoachingQuestion | null>(null)
  const [newSources, setNewSources] = useState<string[]>([])
  const [bulkPushing, setBulkPushing] = useState(false)
  const [bulkArchiving, setBulkArchiving] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [confirmingDeleteAll, setConfirmingDeleteAll] = useState(false)
  const [isQuestionBankExpanded, setIsQuestionBankExpanded] = useState(false)
  const [isAssignedHomeworkExpanded, setIsAssignedHomeworkExpanded] =
    useState(false)

  // Date range for distributing homework across days
  const [startDate, setStartDate] = useState<string>(() =>
    formatDateISO(getUpcomingMonday()),
  )
  const [endDate, setEndDate] = useState<string>(() =>
    formatDateISO(getUpcomingSaturday()),
  )

  // Ordered homework from drag-and-drop in AssignedHomeworkTable
  const [orderedHomework, setOrderedHomework] = useState<
    CoachingHomeworkWithProblem[]
  >([])

  // Calculate tab counts and filter questions
  const {
    filteredQuestions,
    tabs,
    _assignedQuestionIds,
    _doneBeforeQuestionIds,
  } = useMemo(() => {
    const assignedIds = new Set(
      (client.assignedHomework || [])
        .filter((a) => a.status === 'active' && a.type === 'PROBLEM')
        .map((a) => a.problemId)
        .filter((id): id is string => id != null),
    )

    const doneBeforeIds = new Set(
      (client.questionHistory || [])
        .filter((h) => h.doneBefore)
        .map((h) => h.questionId),
    )

    let filtered: CoachingQuestion[]
    switch (activeTab) {
      case 'easy':
        filtered = questions.filter((q) => q.difficulty?.name === 'Easy')
        break
      case 'medium':
        filtered = questions.filter((q) => q.difficulty?.name === 'Medium')
        break
      case 'hard':
        filtered = questions.filter((q) => q.difficulty?.name === 'Hard')
        break
      case 'notAssigned':
        filtered = questions.filter((q) => !assignedIds.has(q.id))
        break
      case 'doneBefore':
        filtered = questions.filter((q) => doneBeforeIds.has(q.id))
        break
      default:
        filtered = questions
    }

    const tabsData = [
      { id: 'all', label: 'All', count: questions.length },
      {
        id: 'easy',
        label: 'Easy',
        count: questions.filter((q) => q.difficulty?.name === 'Easy').length,
      },
      {
        id: 'medium',
        label: 'Medium',
        count: questions.filter((q) => q.difficulty?.name === 'Medium').length,
      },
      {
        id: 'hard',
        label: 'Hard',
        count: questions.filter((q) => q.difficulty?.name === 'Hard').length,
      },
      {
        id: 'notAssigned',
        label: 'Not Assigned',
        count: questions.filter((q) => !assignedIds.has(q.id)).length,
      },
      { id: 'doneBefore', label: 'Done Before', count: doneBeforeIds.size },
    ]

    return {
      filteredQuestions: filtered,
      tabs: tabsData,
      _assignedQuestionIds: assignedIds,
      _doneBeforeQuestionIds: doneBeforeIds,
    }
  }, [questions, activeTab, client.assignedHomework, client.questionHistory])

  const handleAddQuestion = async (questionData: {
    externalId?: string
    name: string
    difficulty?: string | null
    premiumRequired?: boolean
    type?: ProblemType
    problemTagSlugs?: string[]
    acceptanceRate?: number | null
    freqBar?: number | null
    totalAccepted?: number | null
    totalSubmitted?: number | null
    companies?: string[]
    url?: string | null
    editorialUrl?: string | null
    source: string
    sourceIcon?: string | null
  }) => {
    // Convert null to undefined for API compatibility
    // Convert empty string or null difficulty to undefined
    await addQuestion({
      ...questionData,
      difficulty:
        questionData.difficulty && questionData.difficulty.trim() !== ''
          ? questionData.difficulty
          : undefined,
      acceptanceRate: questionData.acceptanceRate ?? undefined,
      freqBar: questionData.freqBar ?? undefined,
      totalAccepted: questionData.totalAccepted ?? undefined,
      totalSubmitted: questionData.totalSubmitted ?? undefined,
      url: questionData.url ?? undefined,
      editorialUrl: questionData.editorialUrl ?? undefined,
      sourceIcon: questionData.sourceIcon ?? undefined,
    })
  }

  const handleUpdateApproaches = useCallback(
    async (questionId: string, approaches: QuestionApproach[]) => {
      await updateQuestion(questionId, { approaches })
      toast.success('Approaches updated successfully')
      // Update selected question with new approaches
      setSelectedQuestion((prev) => (prev ? { ...prev, approaches } : null))
    },
    [updateQuestion],
  )

  const handleDeleteQuestion = useCallback(
    async (questionId: string) => {
      await deleteQuestion(questionId)
    },
    [deleteQuestion],
  )

  // Row click → opens Edit Details modal
  const handleSelectQuestion = useCallback((question: CoachingQuestion) => {
    setSelectedQuestion(question)
    setShowEditQuestion(true)
  }, [])

  // From Edit modal, switch to Approaches modal
  const handleSwitchToApproaches = useCallback(() => {
    setShowEditQuestion(false)
    setShowApproachesModal(true)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setShowEditQuestion(false)
    if (!showApproachesModal) {
      setSelectedQuestion(null)
    }
  }, [showApproachesModal])

  const handleCloseApproachesModal = useCallback(() => {
    setShowApproachesModal(false)
    setSelectedQuestion(null)
  }, [])

  const handleAddSource = useCallback((source: string) => {
    setNewSources((prev) => [...prev, source])
  }, [])

  const handleUpdateQuestion = useCallback(
    async (
      id: string,
      data: {
        externalId?: string
        name: string
        difficulty?: string | null
        premiumRequired?: boolean
        type?: ProblemType
        problemTagSlugs?: string[]
        acceptanceRate?: number | null
        freqBar?: number | null
        totalAccepted?: number | null
        totalSubmitted?: number | null
        companies?: string[]
        url?: string | null
        editorialUrl?: string | null
        source: string
        sourceIcon?: string | null
      },
    ) => {
      await updateQuestion(id, {
        ...data,
        url: data.url ?? undefined,
        editorialUrl: data.editorialUrl ?? undefined,
        sourceIcon: data.sourceIcon ?? undefined,
      })
      toast.success('Problem updated successfully')
      // Update the selected question with new data. `data` carries
      // `problemTagSlugs` (plain slugs), but CoachingQuestion exposes descriptor
      // tags as `problemTags` ({ slug, name }); rebuild that shape from the
      // edited slugs so the cached question doesn't keep pre-edit tags (or a
      // stray `problemTagSlugs`) until the list refetches. Only rebuild when the
      // edit actually carried slugs; otherwise keep the previous tags.
      setSelectedQuestion((prev) => {
        if (!prev) return null
        const { problemTagSlugs, ...rest } = data
        return {
          ...prev,
          ...rest,
          difficulty: (data.difficulty?.toUpperCase() ||
            null) as CoachingQuestion['difficulty'],
          problemTags:
            problemTagSlugs !== undefined
              ? problemTagSlugs.map((slug) => {
                  const definition = PROBLEM_TAG_VOCABULARY.find(
                    (t) => t.slug === slug,
                  )
                  return { slug, name: definition?.name ?? slug }
                })
              : prev.problemTags,
        }
      })
    },
    [updateQuestion],
  )

  const allSources = useMemo(
    () => [...new Set([...sources, ...newSources])].sort(),
    [sources, newSources],
  )

  // Count only active homework that hasn't been pushed to Notion
  const activeHomework = (client.assignedHomework || []).filter(
    (a) => a.status === 'active' && !a.notionPageId,
  )
  const activeHomeworkCount = activeHomework.length
  const responsePushableHomework = activeHomework.filter(
    (assignment) =>
      canPushHomeworkToNotion(assignment) &&
      canRetryNotionDelivery(observedDeliveryResults[assignment.id]),
  )
  const pushableHomework = responsePushableHomework.filter(
    (assignment) =>
      Boolean(client.notionDatabaseId) ||
      hasHomeworkActivationMetadata(assignment),
  )
  const pushableHomeworkCount = pushableHomework.length
  const legacyPushableHomework = pushableHomework.filter(
    isHistoricalLegacyHomework,
  )
  const legacyPushableHomeworkCount = legacyPushableHomework.length

  // Count problems (type === 'PROBLEM') in active homework
  const activeProblemCount = useMemo(() => {
    return activeHomework.filter((a) => a.type === 'PROBLEM').length
  }, [activeHomework])

  // Calculate distribution estimate for date range
  const distributionEstimate = useMemo(() => {
    if (legacyPushableHomeworkCount === 0) return null

    const days = getDaysInRange(parseDateISO(startDate), parseDateISO(endDate))
    if (days.length === 0) return null

    const itemsPerDay = distributeItemsAcrossDays(
      legacyPushableHomeworkCount,
      days.length,
    )

    // Group consecutive days with the same count
    const groups: { count: number; days: number }[] = []
    let currentCount = itemsPerDay[0]
    let currentDays = 1

    for (let i = 1; i < itemsPerDay.length; i++) {
      if (itemsPerDay[i] === currentCount) {
        currentDays++
      } else {
        if (currentCount > 0) {
          groups.push({ count: currentCount, days: currentDays })
        }
        currentCount = itemsPerDay[i]
        currentDays = 1
      }
    }
    if (currentCount > 0) {
      groups.push({ count: currentCount, days: currentDays })
    }

    // Format the distribution as an array of objects with count for color coding
    if (groups.length === 0) return null

    return groups.map((g, index) => ({
      days: g.days,
      count: g.count,
      isFirst: index === 0,
    }))
  }, [legacyPushableHomeworkCount, startDate, endDate])

  // Handler for when homework order changes via drag-and-drop
  const handleOrderChange = useCallback(
    (ordered: CoachingHomeworkWithProblem[]) => {
      setOrderedHomework(ordered)
    },
    [],
  )

  /**
   * Distributes homework items across a date range, assigning due dates.
   * Top items in the ordered list get earlier dates.
   */
  const distributeHomeworkWithDates = useCallback(
    (
      homework: CoachingHomeworkWithProblem[],
      start: string,
      end: string,
    ): { homework: CoachingHomeworkWithProblem; dueDate: string }[] => {
      if (homework.length === 0) return []

      const days = getDaysInRange(parseDateISO(start), parseDateISO(end))
      if (days.length === 0)
        return homework.map((hw) => ({ homework: hw, dueDate: start }))

      const itemsPerDay = distributeItemsAcrossDays(
        homework.length,
        days.length,
      )

      let dayIndex = 0
      let countInCurrentDay = 0

      return homework.map((hw) => {
        const dueDate = formatDateISO(days[dayIndex])
        countInCurrentDay++

        if (countInCurrentDay >= itemsPerDay[dayIndex]) {
          dayIndex++
          countInCurrentDay = 0
        }

        return { homework: hw, dueDate }
      })
    },
    [],
  )

  // Bulk push all active homework to Notion with distributed dates
  const handlePushAllToNotion = useCallback(async () => {
    // Use the ordered list from drag-and-drop, or fall back to activeHomework
    const homeworkToPush = (
      orderedHomework.length > 0 ? orderedHomework : pushableHomework
    ).filter(
      (assignment) =>
        canPushHomeworkToNotion(assignment) &&
        canRetryNotionDelivery(observedDeliveryResults[assignment.id]) &&
        (Boolean(client.notionDatabaseId) ||
          hasHomeworkActivationMetadata(assignment)),
    )

    if (homeworkToPush.length === 0) {
      toast.error('No active homework to push.')
      return
    }

    // Only historical rows accept caller-supplied dates. Canonical rows use
    // their authoritative plan-day date and must not consume a date slot.
    const legacyDueDateById = new Map(
      distributeHomeworkWithDates(
        homeworkToPush.filter(isHistoricalLegacyHomework),
        startDate,
        endDate,
      ).map(({ homework, dueDate }) => [homework.id, dueDate]),
    )

    setBulkPushing(true)
    let successCount = 0
    let transportErrorCount = 0
    const observed: Record<NonSuccessDeliveryState, number> = {
      queued: 0,
      creating: 0,
      unknown: 0,
      failed: 0,
      archived_without_push: 0,
    }

    try {
      for (const homework of homeworkToPush) {
        try {
          const dueDate = legacyDueDateById.get(homework.id)
          const result = (await notionPushMutation.mutateAsync({
            id: client.id,
            homeworkId: homework.id,
            ...(dueDate === undefined ? {} : { dueDate }),
          })) as unknown as NotionDeliveryUiResult
          observeDeliveryResult(homework.id, result)
          if (
            result.success &&
            (result.state === 'succeeded' || result.state === 'reconciled')
          ) {
            successCount++
          } else if (
            NON_SUCCESS_DELIVERY_STATES.includes(
              result.state as NonSuccessDeliveryState,
            )
          ) {
            observed[result.state as NonSuccessDeliveryState]++
          } else {
            transportErrorCount++
          }
        } catch {
          transportErrorCount++
        }
      }

      if (successCount > 0) {
        toast.success(
          `Pushed ${successCount} item${successCount > 1 ? 's' : ''} to Notion`,
        )
      }
      // Enqueue is not success: queued work is reported separately and its
      // pages are created by the background worker.
      if (observed.queued > 0) {
        toast.success(
          `${observed.queued} queued for Notion delivery — pages are created in the background`,
        )
      }
      const stateLabels: Record<
        Exclude<NonSuccessDeliveryState, 'queued'>,
        string
      > = {
        creating: 'creating — do not retry; use operator recovery if stranded',
        unknown: 'unknown — do not retry; use operator recovery',
        failed: 'failed',
        archived_without_push: 'archived without push',
      }
      for (const state of NON_SUCCESS_DELIVERY_STATES) {
        if (state === 'queued') continue
        const count = observed[state]
        if (count > 0) {
          toast.error(`${count} ${stateLabels[state]}`)
        }
      }
      if (transportErrorCount > 0) {
        toast.error(
          `Failed to request ${transportErrorCount} item${transportErrorCount > 1 ? 's' : ''}`,
        )
      }
      await refreshAssignmentViews()
    } finally {
      setBulkPushing(false)
    }
  }, [
    pushableHomework,
    observedDeliveryResults,
    orderedHomework,
    startDate,
    endDate,
    distributeHomeworkWithDates,
    client.notionDatabaseId,
    client.id,
    notionPushMutation,
    observeDeliveryResult,
    refreshAssignmentViews,
  ])

  // Bulk archive all active homework without pushing to Notion
  const handleArchiveAll = useCallback(async () => {
    if (activeHomework.length === 0) {
      toast.error('No active homework to archive.')
      return
    }

    setBulkArchiving(true)
    let successCount = 0
    let errorCount = 0

    try {
      for (const homework of activeHomework) {
        try {
          await archiveHomeworkMutation.mutateAsync({
            id: client.id,
            homeworkId: homework.id,
          })
          successCount++
        } catch {
          errorCount++
        }
      }

      if (successCount > 0) {
        toast.success(
          `Archived ${successCount} item${successCount > 1 ? 's' : ''}`,
        )
      }
      if (errorCount > 0) {
        toast.error(
          `Failed to archive ${errorCount} item${errorCount > 1 ? 's' : ''}`,
        )
      }
      await refreshAssignmentViews()
    } finally {
      setBulkArchiving(false)
    }
  }, [
    activeHomework,
    client.id,
    archiveHomeworkMutation,
    refreshAssignmentViews,
  ])

  // Bulk delete all active homework - requires confirmation
  const handleDeleteAllActive = useCallback(async () => {
    if (activeHomework.length === 0) {
      toast.error('No active homework to delete.')
      return
    }

    // First click shows confirmation, second click executes
    if (!confirmingDeleteAll) {
      setConfirmingDeleteAll(true)
      if (confirmDeleteAllTimeoutRef.current) {
        clearTimeout(confirmDeleteAllTimeoutRef.current)
      }
      // Reset after 3 seconds if not confirmed
      confirmDeleteAllTimeoutRef.current = setTimeout(() => {
        confirmDeleteAllTimeoutRef.current = null
        setConfirmingDeleteAll(false)
      }, 3000)
      return
    }

    if (confirmDeleteAllTimeoutRef.current) {
      clearTimeout(confirmDeleteAllTimeoutRef.current)
      confirmDeleteAllTimeoutRef.current = null
    }
    setConfirmingDeleteAll(false)
    setBulkDeleting(true)
    let successCount = 0
    let errorCount = 0

    try {
      for (const homework of activeHomework) {
        try {
          await onRemoveHomework(homework.id)
          successCount++
        } catch {
          errorCount++
        }
      }

      if (successCount > 0) {
        toast.success(
          `Deleted ${successCount} item${successCount > 1 ? 's' : ''}`,
        )
      }
      if (errorCount > 0) {
        toast.error(
          `Failed to delete ${errorCount} item${errorCount > 1 ? 's' : ''}`,
        )
      }
    } finally {
      setBulkDeleting(false)
    }
  }, [activeHomework, onRemoveHomework, confirmingDeleteAll])

  return (
    <div className="space-y-6">
      {showTrayNotice && (
        <div
          role="status"
          className="border-primary/30 bg-primary/5 text-foreground rounded-lg border px-4 py-3 text-sm"
        >
          Added to tray. Open{' '}
          <a
            href={curriculumHref()}
            onClick={selectV2ForCurriculum}
            className="text-primary font-medium underline underline-offset-2"
          >
            Curriculum
          </a>{' '}
          to place and publish before the client can see it.
        </div>
      )}
      {/* Master Questions Section */}
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="border-border flex items-center justify-between border-b p-4">
          <button
            type="button"
            onClick={() => setIsQuestionBankExpanded(!isQuestionBankExpanded)}
            className="flex flex-1 items-center gap-2 transition-opacity hover:opacity-80"
          >
            <Database size={18} className="text-muted-foreground" />
            <h2 className="text-foreground text-lg font-semibold">
              Question Bank
            </h2>
            <span className="bg-muted text-muted-foreground ml-2 rounded px-2 py-0.5 text-xs">
              {filteredQuestions.length}{' '}
              {filteredQuestions.length === 1 ? 'question' : 'questions'}
              {totalCount !== null && totalCount > questions.length && (
                <span className="ml-1">of {totalCount}</span>
              )}
            </span>
            {loadingMore && (
              <span className="text-muted-foreground flex items-center gap-1 text-xs">
                <RefreshCw size={12} className="animate-spin" />
                Loading more...
              </span>
            )}
          </button>
          <div className="flex items-center gap-2">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                setShowAddQuestion(true)
              }}
              size="sm"
            >
              <Plus size={14} className="mr-1" />
              Add Question
            </Button>
            {isQuestionBankExpanded ? (
              <ChevronUp size={20} className="text-muted-foreground" />
            ) : (
              <ChevronDown size={20} className="text-muted-foreground" />
            )}
          </div>
        </div>
        {isQuestionBankExpanded && (
          <div className="p-4">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            <div className="mt-4">
              {loading ? (
                <div className="text-muted-foreground py-8 text-center">
                  Loading questions...
                </div>
              ) : (
                <MasterQuestionsTable
                  questions={filteredQuestions}
                  sources={allSources}
                  onDeleteQuestion={handleDeleteQuestion}
                  onSelectQuestion={handleSelectQuestion}
                  onUpdateQuestion={updateQuestion}
                  questionHistory={client.questionHistory}
                  assignedHomework={client.assignedHomework}
                  onAssignHomework={handleAssignProblem}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Study Plan Widget */}
      <StudyPlanWidget
        assignedHomework={client.assignedHomework || []}
        onAssignProblems={handleAssignMultipleProblems}
      />

      {/* Source Content Widget (Reading Tasks) */}
      <SourceContentWidget
        assignedHomework={client.assignedHomework || []}
        onAssignSourceContent={handleAssignSourceContent}
        onAssignSourceContentItem={handleAssignSourceContentItem}
      />

      {/* Standalone Tasks Widget (Tasks without source associations) */}
      <StandaloneTasksWidget
        assignedHomework={client.assignedHomework || []}
        onAssignTask={handleAssignStandaloneTask}
      />

      {/* Assigned Homework Section */}
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="border-border flex items-center justify-between border-b p-4">
          <button
            type="button"
            onClick={() =>
              setIsAssignedHomeworkExpanded(!isAssignedHomeworkExpanded)
            }
            className="flex flex-1 items-center gap-2 transition-opacity hover:opacity-80"
          >
            <BookOpen size={18} className="text-muted-foreground" />
            <h2 className="text-foreground text-lg font-semibold">
              Assigned Homework
            </h2>
            <span className="bg-muted text-muted-foreground ml-2 rounded px-2 py-0.5 text-xs">
              {activeProblemCount} problem{activeProblemCount !== 1 ? 's' : ''}
            </span>
            <span className="bg-muted text-muted-foreground ml-2 rounded px-2 py-0.5 text-xs">
              {activeHomeworkCount} active
            </span>
          </button>
          <div className="flex items-center gap-2">
            {activeHomeworkCount > 0 && (
              <>
                {/* Date range pickers for distribution */}
                {legacyPushableHomeworkCount > 0 && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <label
                        htmlFor="start-date"
                        className="text-muted-foreground text-xs"
                      >
                        Start
                      </label>
                      <input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="border-border bg-background text-foreground rounded border px-2 py-1 text-xs"
                        aria-label="Start date for homework distribution"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label
                        htmlFor="end-date"
                        className="text-muted-foreground text-xs"
                      >
                        End
                      </label>
                      <input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="border-border bg-background text-foreground rounded border px-2 py-1 text-xs"
                        aria-label="End date for homework distribution"
                      />
                    </div>
                    {distributionEstimate && (
                      <div className="flex flex-col items-start text-xs">
                        {distributionEstimate.map((group, index) => {
                          const dayText = group.days === 1 ? 'day' : 'days'
                          const questionText =
                            group.count === 1 ? 'question' : 'questions'

                          // Color coding based on question count
                          let countColor = ''
                          if (group.count <= 3) {
                            countColor = 'text-green-600 dark:text-green-400'
                          } else if (group.count <= 5) {
                            countColor = 'text-yellow-600 dark:text-yellow-400'
                          } else {
                            countColor = 'text-red-600 dark:text-red-400'
                          }

                          const prefix = group.isFirst ? '(' : '+ '
                          const suffix =
                            distributionEstimate.length === 1
                              ? ')'
                              : index === distributionEstimate.length - 1
                                ? ')'
                                : ''

                          return (
                            <span
                              key={`${group.days}-${group.count}-${index}`}
                              className="text-muted-foreground"
                            >
                              {prefix}
                              {group.days} {dayText} with{' '}
                              <span className={countColor}>
                                {group.count} {questionText}
                              </span>
                              {suffix}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteAllActive()
                  }}
                  disabled={bulkPushing || bulkDeleting}
                  className={
                    confirmingDeleteAll
                      ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                      : 'text-destructive hover:bg-destructive/10 hover:text-destructive'
                  }
                >
                  {bulkDeleting ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Trash2 size={14} className="mr-1.5" />
                  )}
                  {confirmingDeleteAll ? 'Click to Confirm' : 'Delete All'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleArchiveAll()
                  }}
                  disabled={bulkPushing || bulkArchiving || bulkDeleting}
                  title="Archive all active homework without pushing to Notion"
                >
                  {bulkArchiving ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Archive size={14} className="mr-1.5" />
                  )}
                  Archive All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePushAllToNotion()
                  }}
                  disabled={
                    bulkPushing ||
                    bulkArchiving ||
                    bulkDeleting ||
                    pushableHomeworkCount === 0
                  }
                  title={
                    pushableHomeworkCount > 0
                      ? 'Push all eligible homework to Notion'
                      : !client.notionDatabaseId &&
                          responsePushableHomework.length > 0
                        ? 'Configure Notion Database ID first'
                        : 'Publish canonical assignments in Curriculum before pushing to Notion'
                  }
                >
                  {bulkPushing ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Send size={14} className="mr-1.5" />
                  )}
                  Push All
                </Button>
              </>
            )}
            {isAssignedHomeworkExpanded ? (
              <ChevronUp size={20} className="text-muted-foreground" />
            ) : (
              <ChevronDown size={20} className="text-muted-foreground" />
            )}
          </div>
        </div>
        {isAssignedHomeworkExpanded && (
          <div className="p-4">
            <AssignedHomeworkTable
              assignments={client.assignedHomework || []}
              onRemove={onRemoveHomework}
              onRefresh={onRefresh}
              clientId={client.id}
              notionDatabaseId={client.notionDatabaseId}
              notionTemplates={client.notionTemplates}
              defaultDueDate={startDate}
              onOrderChange={handleOrderChange}
              observedDeliveryResults={observedDeliveryResults}
              onDeliveryResult={observeDeliveryResult}
              onAuthoritativeDeliveryRefresh={
                acceptAuthoritativeDeliveryRefresh
              }
            />
          </div>
        )}
      </div>

      {/* Add Question Modal */}
      <AddQuestionModal
        isOpen={showAddQuestion}
        onClose={() => setShowAddQuestion(false)}
        onSubmit={handleAddQuestion}
        sources={allSources}
        onAddSource={handleAddSource}
      />

      {/* Edit Question Modal - opens on row click */}
      <EditQuestionModal
        isOpen={showEditQuestion}
        onClose={handleCloseEditModal}
        question={selectedQuestion}
        onSubmit={handleUpdateQuestion}
        sources={allSources}
        onAddSource={handleAddSource}
        onEditApproaches={handleSwitchToApproaches}
      />

      {/* Approaches Modal - opens from approaches column or from Edit modal */}
      <ApproachesModal
        isOpen={showApproachesModal}
        question={selectedQuestion}
        onClose={handleCloseApproachesModal}
        onUpdateApproaches={handleUpdateApproaches}
        allTags={tags}
      />
    </div>
  )
}
